import {
  ActionIcon,
  Alert,
  Badge,
  CopyButton,
  Divider,
  Group,
  Modal,
  Skeleton,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Tooltip,
} from '@mantine/core';
import { IconCalendarDue, IconCheck, IconCopy } from '@tabler/icons-react';
import type { ReactNode } from 'react';

import { useDebt } from '../api/queries';
import type { ScheduledPayment } from '../api/types';
import { formatDate, formatFullDateTime, formatKopecks, formatMoney, today } from '../lib/format';

interface DebtDetailsProps {
  debtId: string | null;
  onClose: () => void;
}

/**
 * Everything about one debt, the payment schedule included. Details are
 * re-fetched by id, so a debt removed via MCP shows as gone.
 */
export function DebtDetails({ debtId, onClose }: DebtDetailsProps) {
  const { data, isPending, error } = useDebt(debtId);

  return (
    <Modal
      opened={debtId !== null}
      onClose={onClose}
      size="lg"
      padding="lg"
      centered
      title={<Text fw={600}>Долг</Text>}
    >
      {error ? (
        <Alert color="red" variant="light" title="Долг недоступен">
          {error.message}
        </Alert>
      ) : isPending || !data ? (
        <Stack gap="md">
          <Skeleton height={44} radius="sm" />
          <Skeleton height={20} radius="sm" />
          <Skeleton height={120} radius="sm" />
        </Stack>
      ) : (
        <Stack gap="lg">
          <Stack gap={4}>
            <Text fz={32} fw={600} style={{ fontVariantNumeric: 'tabular-nums' }}>
              {formatMoney(data.debt.remaining)}
            </Text>
            <Group gap="xs">
              <Text size="xs" c="dimmed">
                осталось платить · {formatKopecks(data.debt.remaining)} · {data.debt.currency}
              </Text>
            </Group>
          </Stack>

          <Divider />

          <Group grow align="flex-start">
            <Field label="Чей долг">{data.debt.borrower}</Field>
            <Field label="Где взят">{data.debt.lender}</Field>
          </Group>

          <Field label="Комментарий">
            {data.debt.comment ?? (
              <Text size="sm" c="dimmed" fs="italic">
                без комментария
              </Text>
            )}
          </Field>

          <Field label="Заведён">{formatFullDateTime(data.debt.created_at)}</Field>

          <Field label="Идентификатор">
            <Group gap="xs" wrap="nowrap">
              <Text size="sm" ff="monospace" style={{ wordBreak: 'break-all' }}>
                {data.debt.id}
              </Text>
              <CopyIdButton value={data.debt.id} />
            </Group>
          </Field>

          <Divider />

          <Schedule payments={data.schedule} />
        </Stack>
      )}
    </Modal>
  );
}

/**
 * The schedule is the debt: only unpaid instalments are on it, so the sum of the
 * table is exactly what is left to pay.
 */
function Schedule({ payments }: { payments: ScheduledPayment[] }) {
  const now = today();

  return (
    <Stack gap="sm">
      <Group gap="sm" wrap="nowrap">
        <ThemeIcon size="md" radius="md" variant="light">
          <IconCalendarDue size={18} stroke={1.5} />
        </ThemeIcon>
        <div>
          <Text fw={600} size="sm">
            График платежей
          </Text>
          <Text size="xs" c="dimmed">
            только неоплаченные, суммы минимальные
          </Text>
        </div>
      </Group>

      {payments.length === 0 ? (
        <Group gap="xs" py="sm">
          <Badge color="teal" size="sm" leftSection={<IconCheck size={12} stroke={2} />}>
            закрыт
          </Badge>
          <Text size="sm" c="dimmed">
            платежей не осталось
          </Text>
        </Group>
      ) : (
        <Table.ScrollContainer minWidth={360}>
          <Table verticalSpacing="xs" horizontalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>
                  <Text size="xs" c="dimmed" fw={600} tt="uppercase">
                    Когда платить
                  </Text>
                </Table.Th>
                <Table.Th ta="right">
                  <Text size="xs" c="dimmed" fw={600} tt="uppercase">
                    Сколько
                  </Text>
                </Table.Th>
              </Table.Tr>
            </Table.Thead>

            <Table.Tbody>
              {payments.map((payment) => (
                <Table.Tr key={payment.id}>
                  <Table.Td>
                    <Group gap="xs" wrap="nowrap">
                      <Text size="sm" style={{ whiteSpace: 'nowrap' }}>
                        {formatDate(payment.due_on)}
                      </Text>
                      {payment.due_on < now && (
                        <Badge color="red" size="xs">
                          просрочен
                        </Badge>
                      )}
                    </Group>
                  </Table.Td>
                  <Table.Td ta="right">
                    <Text
                      size="sm"
                      fw={600}
                      style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
                    >
                      {formatMoney(payment.amount)}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
    </Stack>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Stack gap={4}>
      <Text size="xs" tt="uppercase" fw={600} c="dimmed" style={{ letterSpacing: '0.06em' }}>
        {label}
      </Text>
      {typeof children === 'string' ? <Text size="sm">{children}</Text> : children}
    </Stack>
  );
}

function CopyIdButton({ value }: { value: string }) {
  return (
    <CopyButton value={value} timeout={1500}>
      {({ copied, copy }) => (
        <Tooltip label={copied ? 'Скопировано' : 'Скопировать id'} withArrow>
          <ActionIcon
            variant="subtle"
            color={copied ? 'teal' : 'gray'}
            aria-label="Скопировать идентификатор"
            onClick={copy}
          >
            {copied ? <IconCheck size={16} stroke={1.5} /> : <IconCopy size={16} stroke={1.5} />}
          </ActionIcon>
        </Tooltip>
      )}
    </CopyButton>
  );
}
